/* eslint-disable max-len */
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  ExclamationIcon,
  PlusIcon,
  RefreshIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  XIcon,
} from "@heroicons/react/solid";
import _values from "lodash/values";
import _cloneDeep from "lodash/cloneDeep";
import _uniqBy from "lodash/uniqBy";
import _omit from "lodash/omit";

import CorporateSelection from "./CorporateSelection";
import ModalWrapper from "components/lib/Shared/ModalWrapper";
import { openModal } from "state/slices/modals";
import CorporateShareholderTable from "./CorporateShareholderTable";
import {
  INDIVIDUALS,
  CORPORATES,
  CORPORATES_ID_TYPES,
  CORPORATE_MAX_VALUE,
  CORPORATE_SHAREHOLDERS_LIMIT,
} from "../../SFLdata/config/constants";
import {
  addBeneficialOwnerAsync,
  deleteBeneficialOwnerAsync,
  editBeneficialOwnerAsync,
  getAllBeneficialOwners,
  updateDropdownEntryStatusAsync,
} from "state/slices/tables/beneficialOwnersTableSlice";
import { clearEmpties, getFullName, roundNumber } from "utils";
import {
  addMultipleCorporateShareholder,
  deleteCorporateShareholder,
  resetCorporateShareholderTable,
} from "state/slices/tables/corporateShareholderTableSlice";
import {
  setCurrentJSGroup,
  setJointShareholders,
  deleteJointShareholder,
  resetJointShareholderTable,
} from "state/slices/tables/corporateJointShareholderTableSlice";
import { deleteFiles } from "utils/filesUtils";
import { useParams } from "react-router-dom";
import Button from "components/lib/Shared/Button";
import CapitalChangeWarning from "../CapitalChangeWarning";

const CorporateComponent = (props) => {
  const {
    shareholderWithBO,
    selectedShareholder,
    idType,
    setShowBeneficialOwnersBtn,
    setSelectedDropdownBO,
    isInUpdateCompanyDetailsContext,
    parentInfo,
  } = props;

  const [mode, setMode] = useState({ id: "", editing: false });
  const [subIDType, setSubIDType] = useState("");
  const dispatch = useDispatch();
  const [currentOfficial, setCurrentOfficial] = useState({});
  const [allottedShares, setAllottedShares] = useState(0);
  const [allottedVotingRights, setAllottedVotingRights] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [showActiveBanner, setShowActiveBanner] = useState(true);
  const { company } = useSelector((state) => state.companySlice);
  const params = useParams();
  const companyId = company?._id ?? params?.companyId;

  // NEW: Check corporate status
  const corporateStatus =
    selectedShareholder?.corporateStatus ||
    selectedShareholder?.identificationDetails?.corporateStatus ||
    "Active"; // Default to Active if not specified
  const isCorporateActive = corporateStatus === "Active";

  const [showCorporateBtn, setShowCorporateBtn] = useState(true);
  const { shareholders: jointShareholders, currentJSGroup } = useSelector(
    (state) => state
  ).corporateJointShareholderTableSlice;

  const { modals } = useSelector((state) => state.modalsSlice);
  let corporateShareholders = useSelector(
    (state) => state.corporateShareholderTableSlice
  ).filter(
    (shareholder) =>
      shareholder.shareholderWithBO === shareholderWithBO &&
      !shareholder?.linkToCompany
  );
  const { beneficialOwners } = useSelector(
    (state) => state.beneficialOwnersTableSlice
  );

  const beneficialOwnersDropdown = beneficialOwners.filter(
    (bo) => bo?.showInBoDropdown
  );

  const expectedTotalShares = parentInfo?.targetTotal || CORPORATE_MAX_VALUE;
  const expectedTotalVoting = parentInfo?.targetTotal || CORPORATE_MAX_VALUE;
  const hasCapitalChange = !!parentInfo;
  const isCapitalIncrease = parentInfo?.isIncrease;
  const isCapitalDecrease = parentInfo?.isDecrease;

  const handleEditAndDeleteJointShareholder = (row) => {
    let jointShareholderForEdit = jointShareholders.filter(
      (js) =>
        js.JSGroupID === row.JSGroupID && js.idType !== "Joint Shareholder"
    );
    if (jointShareholderForEdit.length === 0) {
      jointShareholderForEdit = beneficialOwners.filter(
        (official) =>
          !official.linkToCompany &&
          official.JSGroupID === row.JSGroupID &&
          (row?.id !== official?.id || row?._id !== official?._id) &&
          official.idType !== "Joint Shareholder"
      );
    }
    jointShareholderForEdit.map((js) => dispatch(deleteJointShareholder(js)));
    dispatch(setCurrentJSGroup(row));
    dispatch(setJointShareholders(jointShareholderForEdit));
    setSubIDType("Joint Shareholder");
    setShowCorporateBtn(false);
  };

  const isMinorityRowExists = () =>
    corporateShareholders.find((co) => co?.remainingMinorityShareholders > 0);

  const handleEdit = (row) => {
    // Block edit if corporate is Active
    if (isCorporateActive) {
      alert(
        "Cannot edit shareholders of an active corporate. The data is synced from the corporate registry."
      );
      return;
    }

    if (
      !row.remainingMinorityShareholders &&
      corporateShareholders.length > CORPORATE_SHAREHOLDERS_LIMIT &&
      isMinorityRowExists() &&
      !row?.isReadOnly
    ) {
      alert("Please first remove the minority row");
      return;
    }
    if (row.idType === "Joint Shareholder") {
      handleEditAndDeleteJointShareholder(row);
      return;
    }
    if (!row.isViewOnly && !row?.isReadOnly) setIsViewOnly(false);
    else setIsViewOnly(true);
    setCurrentOfficial(row);
    setMode({ editing: true, id: row?.id || row?._id });
    dispatch(openModal({ modalName: "corporate_edit_modal" }));
  };

  const handleDelete = async (row) => {
    // Block delete if corporate is Active
    if (isCorporateActive) {
      alert(
        "Cannot delete shareholders of an active corporate. The data is synced from the corporate registry."
      );
      return;
    }

    if (
      !row.remainingMinorityShareholders &&
      corporateShareholders.length > CORPORATE_SHAREHOLDERS_LIMIT &&
      isMinorityRowExists()
    ) {
      alert("Please first remove the minority row");
      return;
    }
    if (row.idType === "Joint Shareholder") {
      handleEditAndDeleteJointShareholder(row);
      return;
    }
    dispatch(deleteCorporateShareholder(row));
    setShowCorporateBtn(true);
    setCurrentOfficial({});
  };

  useEffect(() => {
    if (!currentJSGroup?.id || !currentJSGroup?._id) {
      setSubIDType("");
    }
  }, [currentJSGroup]);

  useEffect(() => {
    setShowCorporateBtn(true);
  }, [idType]);

  useEffect(() => {
    if (
      !modals.includes("corporate_edit_modal") &&
      (currentOfficial?.id || currentOfficial?._id)
    ) {
      setMode({ id: "", editing: false });
      setCurrentOfficial({});
      setShowCorporateBtn(true);
    }
  }, [modals, currentOfficial]);

  useEffect(() => {
    const calculateAllottedShares = () => {
      let { tempAllottedShares, tempAllottedVotingRights } =
        corporateShareholders.reduce(
          (acc, bo) => ({
            tempAllottedShares:
              acc.tempAllottedShares +
              Number(
                bo.beneficialOwnershipForm?.directPercentShareholding ?? 0
              ) +
              Number(
                bo.beneficialOwnershipForm?.indirectPercentShareholding ?? 0
              ) +
              Number(bo?.totalMinorityShareholdingPercent ?? 0),
            tempAllottedVotingRights:
              acc.tempAllottedVotingRights +
              Number(
                bo.beneficialOwnershipForm?.directPercentVotingRights ?? 0
              ) +
              Number(
                bo.beneficialOwnershipForm?.indirectPercentVotingRights ?? 0
              ) +
              Number(bo?.totalMinorityVotingPercent ?? 0),
          }),
          { tempAllottedShares: 0, tempAllottedVotingRights: 0 }
        );

      if (currentOfficial?.id || currentOfficial?._id) {
        const { beneficialOwnershipForm } = currentOfficial;
        tempAllottedShares -=
          Number(beneficialOwnershipForm?.directPercentShareholding ?? 0) +
            Number(beneficialOwnershipForm?.indirectPercentShareholding ?? 0) ||
          (currentOfficial?.totalMinorityShareholdingPercent ?? 0);

        tempAllottedVotingRights -=
          Number(beneficialOwnershipForm?.directPercentVotingRights ?? 0) +
            Number(beneficialOwnershipForm?.indirectPercentVotingRights ?? 0) ||
          (currentOfficial?.totalMinorityVotingPercent ?? 0);
      }

      setAllottedShares(roundNumber(tempAllottedShares));
      setAllottedVotingRights(roundNumber(tempAllottedVotingRights));
    };
    calculateAllottedShares();
  }, [currentOfficial, corporateShareholders, mode.editing]);

  const handleCorporateTableSubmit = async () => {
    try {
      setIsSubmitting(true);

      if (hasCapitalChange) {
        const sharesDiff = Math.abs(allottedShares - expectedTotalShares);
        const votingDiff = Math.abs(allottedVotingRights - expectedTotalVoting);

        if (sharesDiff > 0.01 || votingDiff > 0.01) {
          alert(
            `Distribution mismatch:\n` +
              `Expected: ${expectedTotalShares}% shares, ${expectedTotalVoting}% voting\n` +
              `Current: ${allottedShares}% shares, ${allottedVotingRights}% voting\n\n` +
              `Please adjust to match the parent's updated ownership.`
          );
          setIsSubmitting(false);
          return;
        }
      }

      const addToBOTableOrDropdown = async (shareholder) => {
        // ... existing implementation ...
      };

      await Promise.all(corporateShareholders.map(addToBOTableOrDropdown));

      await Promise.all(
        beneficialOwners.map((bo) => {
          if (bo.shareholderWithBO === selectedShareholder._id) {
            return dispatch(
              deleteBeneficialOwnerAsync({
                beneficialOwnerId: bo._id,
                companyId,
                hardDelete: !bo?.isReadOnly,
              })
            );
          }
          return Promise.resolve();
        })
      );

      await Promise.all(
        beneficialOwnersDropdown.map((bo) => {
          if (bo.shareholderWithBO === selectedShareholder._id) {
            return dispatch(
              updateDropdownEntryStatusAsync({
                beneficialOwnerId: bo._id,
                data: { showInBoDropdown: false },
              })
            );
          }
          return Promise.resolve();
        })
      );

      dispatch(resetJointShareholderTable());
      await dispatch(
        updateDropdownEntryStatusAsync({
          beneficialOwnerId: selectedShareholder._id,
          data: { showInBoDropdown: false },
        })
      );

      if (isInUpdateCompanyDetailsContext) {
        await dispatch(
          editBeneficialOwnerAsync({
            companyId,
            beneficialOwnerId: selectedShareholder._id,
            data: {
              ...selectedShareholder,
              _needsReview: false,
              _hasTransactionChanges: false,
              _isChangedParent: false,
            },
          })
        );
      }

      setShowBeneficialOwnersBtn(true);
      const isInBOTable = beneficialOwners.find(
        (bo) => bo._id === shareholderWithBO
      );
      if (!isInBOTable && shareholderWithBO.link) {
        selectedShareholder.showInBoTable = true;
        await dispatch(
          addBeneficialOwnerAsync({
            companyId,
            data: clearEmpties(selectedShareholder),
          })
        );
      }
      dispatch(resetCorporateShareholderTable());
      setSelectedDropdownBO(false);
      setIsSubmitting(false);
      await dispatch(getAllBeneficialOwners({ companyId }));
    } catch (error) {
      console.error("Error submitting corporate shareholders:", error);
      setIsSubmitting(false);
    }
  };

  const isEveryShareholderReadOnly =
    corporateShareholders.length > 0 &&
    corporateShareholders.every((shareholder) => shareholder.isReadOnly);

  const showSubmitListButton =
    (isInUpdateCompanyDetailsContext || !isEveryShareholderReadOnly) &&
    (!currentJSGroup?.JSGroupID || showCorporateBtn) &&
    ((allottedShares === expectedTotalShares &&
      allottedVotingRights === expectedTotalVoting) ||
      (allottedVotingRights === expectedTotalVoting &&
        idType === "Company Limited By Guarantee/NGO") ||
      isInUpdateCompanyDetailsContext);

  return (
    <div className="space-y-4">
      {/* NEW: Corporate Status Banner */}
      {isCorporateActive && showActiveBanner && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 relative">
          <button
            type="button"
            onClick={() => setShowActiveBanner(false)}
            className="absolute top-3 right-3 text-green-600 hover:text-green-800 focus:outline-none"
            aria-label="Close banner"
          >
            <XIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon
                className="h-5 w-5 text-green-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Active Corporate - Data Synced from Registry
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  This corporate is registered and active. Shareholder data is
                  automatically synced from the corporate registry. You cannot
                  manually add, edit, or delete shareholders. Use the "Sync
                  Shareholders" button to pull the latest data.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isCorporateActive && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon
                className="h-5 w-5 text-yellow-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Inactive Corporate - Manual Management
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  This corporate is not active or not registered. You can
                  manually add, edit, or delete shareholders.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Capital Change Warning */}
      {hasCapitalChange && (
        <div
          className={`border-l-4 p-4 ${
            isCapitalIncrease
              ? "bg-blue-50 border-blue-400"
              : "bg-orange-50 border-orange-400"
          }`}
        >
          <div className="flex">
            <div className="flex-shrink-0">
              {isCapitalIncrease ? (
                <ArrowUpIcon
                  className="h-5 w-5 text-blue-400"
                  aria-hidden="true"
                />
              ) : (
                <ArrowDownIcon
                  className="h-5 w-5 text-orange-400"
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="ml-3 flex-1">
              <h3
                className={`text-sm font-medium ${
                  isCapitalIncrease ? "text-blue-800" : "text-orange-800"
                }`}
              >
                {isCapitalIncrease
                  ? "Capital Increase Detected"
                  : "Capital Decrease Detected"}
              </h3>
              <div
                className={`mt-2 text-sm ${
                  isCapitalIncrease ? "text-blue-700" : "text-orange-700"
                }`}
              >
                <p>
                  <strong>{getFullName(selectedShareholder.names)}</strong>{" "}
                  ownership{" "}
                  {isCapitalIncrease ? (
                    <span className="font-semibold text-green-600">
                      increased
                    </span>
                  ) : (
                    <span className="font-semibold text-red-600">
                      decreased
                    </span>
                  )}{" "}
                  from{" "}
                  <strong>{parentInfo.currentChildrenTotal.toFixed(2)}%</strong>{" "}
                  to <strong>{expectedTotalShares.toFixed(2)}%</strong> (
                  {isCapitalIncrease ? "+" : ""}
                  {parentInfo.sharesDifference.toFixed(2)}%).
                </p>
                <p className="mt-1">
                  {isCapitalIncrease ? (
                    <>
                      <strong className="text-blue-800">
                        {Math.abs(parentInfo.sharesDifference).toFixed(2)}%
                      </strong>{" "}
                      additional ownership must be allocated.
                    </>
                  ) : (
                    <>
                      Current beneficial owners hold{" "}
                      <strong className="text-orange-800">
                        {Math.abs(parentInfo.sharesDifference).toFixed(2)}%
                      </strong>{" "}
                      more than the new total. Adjust ownership to match.
                    </>
                  )}
                </p>
                <p className="mt-2 text-xs font-semibold">
                  Target: {expectedTotalShares.toFixed(2)}% shares,{" "}
                  {expectedTotalVoting.toFixed(2)}% voting
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <h2 className="mt-2 text-lg font-medium leading-6 text-gray-900">
        {hasCapitalChange ? (
          <>
            Adjust shareholders of{" "}
            <span className="text-primary">
              {getFullName(selectedShareholder.names)}
            </span>
          </>
        ) : (
          <>
            {isCorporateActive ? "Review" : "Add the"} shareholders of the
            corporate company:{" "}
            <span className="text-primary">
              {getFullName(selectedShareholder.names)}
            </span>
          </>
        )}
      </h2>

      {!hasCapitalChange && !isCorporateActive && (
        <div className="p-4 rounded-md bg-yellow-50">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationIcon
                className="w-5 h-5 text-yellow-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Attention needed
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Please add upto top ten shareholders of the company and
                  thereafter update the particulars of the beneficial owners for
                  those shareholders who meet of the set criteria
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync Button for Active Corporates */}
      {isInUpdateCompanyDetailsContext && isCorporateActive && (
        <div className="flex justify-end mb-2">
          <Button
            type="button"
            onClick={async () => {
              setIsSyncing(true);
              // Fetch latest data from corporate registry
              const resultAction = await dispatch(
                getAllBeneficialOwners({ companyId })
              );
              if (getAllBeneficialOwners.fulfilled.match(resultAction)) {
                const freshBOs = resultAction.payload.items;
                const corporateShareholders = freshBOs.filter(
                  (bo) => bo.shareholderWithBO === shareholderWithBO
                );
                dispatch(resetCorporateShareholderTable());
                dispatch(
                  addMultipleCorporateShareholder(
                    _uniqBy(corporateShareholders, (official) => {
                      if (official?.JSGroupID) return official.JSGroupID;
                      else return official._id;
                    })
                  )
                );
              }
              setIsSyncing(false);
            }}
            className="inline-flex items-center space-x-2"
          >
            <RefreshIcon
              className={`w-4 h-4 mr-2 ${isSyncing ? "animate-spin" : ""}`}
            />
            Sync Shareholders
          </Button>
        </div>
      )}

      {corporateShareholders.length > 0 && (
        <CorporateShareholderTable
          corporateShareholders={corporateShareholders}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          allottedShares={allottedShares}
          allottedVotingRights={allottedVotingRights}
          expectedTotalShares={expectedTotalShares}
          expectedTotalVoting={expectedTotalVoting}
          hasCapitalChange={hasCapitalChange}
          isInUpdateCompanyDetailsContext={isInUpdateCompanyDetailsContext}
          companyId={companyId}
          parentInfo={parentInfo}
          isCorporateActive={isCorporateActive} // NEW: Pass status
        />
      )}

      {(currentOfficial?.id || currentOfficial?._id) && (
        <ModalWrapper
          name="corporate_edit_modal"
          title={
            isViewOnly
              ? "View Corporate Shareholder"
              : "Edit Corporate Shareholder"
          }
          maxWidth="sm:max-w-fit"
        >
          <CorporateSelection
            currentOfficial={currentOfficial}
            corporateIdType={currentOfficial.corporateIdType || idType}
            shareholderWithBO={shareholderWithBO}
            selectedShareholder={selectedShareholder}
            setShowCorporateBtn={setShowCorporateBtn}
            allottedShares={allottedShares}
            allottedVotingRights={allottedVotingRights}
            mode={mode}
            isViewOnly={isViewOnly}
            isInUpdateCompanyDetailsContext={isInUpdateCompanyDetailsContext}
            expectedTotalShares={expectedTotalShares}
            expectedTotalVoting={expectedTotalVoting}
          />
        </ModalWrapper>
      )}

      {!showCorporateBtn && !mode.editing ? (
        <CorporateSelection
          currentOfficial={currentOfficial}
          corporateIdType={currentOfficial.corporateIdType || idType}
          shareholderWithBO={shareholderWithBO}
          selectedShareholder={selectedShareholder}
          setShowCorporateBtn={setShowCorporateBtn}
          allottedShares={allottedShares}
          allottedVotingRights={allottedVotingRights}
          mode={mode}
          subIDType={subIDType}
          jointShareholders={jointShareholders}
          isInUpdateCompanyDetailsContext={isInUpdateCompanyDetailsContext}
          expectedTotalShares={expectedTotalShares}
          expectedTotalVoting={expectedTotalVoting}
        />
      ) : (
        <div className="flex items-center justify-between mt-4">
          <div>
            {/* Hide "Add Shareholder" button if corporate is Active */}
            {!isCorporateActive &&
            (((allottedShares < expectedTotalShares ||
              allottedVotingRights < expectedTotalVoting) &&
              (allottedVotingRights !== expectedTotalVoting ||
                idType !== "Company Limited By Guarantee/NGO") &&
              corporateShareholders.length <= CORPORATE_SHAREHOLDERS_LIMIT) ||
              isInUpdateCompanyDetailsContext) ? (
              <button
                onClick={() => {
                  setShowCorporateBtn(false);
                }}
                className="inline-flex items-center justify-center space-x-2 text-sm font-medium rounded-md text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PlusIcon className="w-6 h-6" aria-hidden="true" />
                {corporateShareholders.length === CORPORATE_SHAREHOLDERS_LIMIT
                  ? " Add Minority"
                  : CORPORATES_ID_TYPES[idType]}
              </button>
            ) : null}
          </div>
          {showSubmitListButton && (
            <Button
              onClick={handleCorporateTableSubmit}
              isLoading={isSubmitting}
            >
              {isInUpdateCompanyDetailsContext
                ? "Save and Proceed"
                : "Submit List"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default CorporateComponent;
